import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../redux/helpers/axios";

// Turn "Men's Fashion" into "mens-fashion"
const slugify = (str) =>
  str
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const ElevenCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState({ name: "", slug: "", img: "" });
  // Stop auto-slug once the user edits the slug by hand
  const [slugTouched, setSlugTouched] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const openModal = () => {
    setError("");
    setSuccess("");
    setShowModal(true);
  };

  // Close modal and reset the form
  const closeModal = () => {
    setShowModal(false);
    setForm({ name: "", slug: "", img: "" });
    setSlugTouched(false);
    setError("");
  };

  // GET all categories -> { success, categories }
  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(`/eleven/categories`);
      setCategories(data.categories);
    } catch (err) {
      // axios throws on non-2xx; backend message is in response.data
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to fetch categories",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleNameChange = (e) => {
    const name = e.target.value;
    setForm((f) => ({
      ...f,
      name,
      slug: slugTouched ? f.slug : slugify(name),
    }));
  };

  const handleSlugChange = (e) => {
    setSlugTouched(true);
    setForm((f) => ({ ...f, slug: slugify(e.target.value) }));
  };

  // POST create category -> { success, category }
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.name.trim() || !form.slug.trim()) {
      setError("Name and slug are required");
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axiosInstance.post(`/eleven/create-category`, {
        name: form.name.trim(),
        slug: form.slug.trim(),
        img: form.img.trim() || null,
      });

      // Newest first, matching the backend's createdAt desc order
      setCategories((prev) => [data.category, ...prev]);
      closeModal();
      setSuccess(data.msg);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || "Failed to create category",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] content-start">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">
          Eleven Categories
        </h1>
        <button
          onClick={openModal}
          className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          Create category
        </button>
      </div>

      {/* Page-level feedback (modal shows its own errors) */}
      {success && <p className="mb-4 text-sm text-green-600">{success}</p>}
      {error && !showModal && (
        <p className="mb-4 text-sm text-red-600">{error}</p>
      )}

      {/* Create modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeModal}
        >
          <form
            onSubmit={handleSubmit}
            // Stop clicks inside the modal from closing it
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
          >
            <h2 className="mb-4 text-lg font-semibold text-gray-900">
              Create category
            </h2>

            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                type="text"
                value={form.name}
                onChange={handleNameChange}
                placeholder="Men's Fashion"
                autoFocus
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              />
            </div>

            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Slug
              </label>
              <input
                type="text"
                value={form.slug}
                onChange={handleSlugChange}
                placeholder="mens-fashion"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              />
            </div>

            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Image URL (optional)
              </label>
              <input
                type="text"
                value={form.img}
                onChange={(e) => setForm({ ...form, img: e.target.value })}
                placeholder="https://..."
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              />
            </div>

            {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitting ? "Creating..." : "Create category"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Category list */}
      {loading ? (
        <p className="text-sm text-gray-500">Loading categories...</p>
      ) : categories.length === 0 ? (
        <p className="text-sm text-gray-500">
          No categories yet. Create your first one above.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3 font-medium">#</th>
                <th className="px-4 py-3 font-medium">Image</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Slug</th>
                <th className="px-4 py-3 font-medium">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categories.map((cat, i) => (
                <tr key={cat.id}>
                  <td className="px-4 py-3 text-gray-500">{i + 1}</td>
                  <td className="px-4 py-3">
                    {cat.img ? (
                      <img
                        src={cat.img}
                        alt={cat.name}
                        className="h-10 w-10 rounded object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-100 text-[10px] text-gray-400">
                        N/A
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {cat.name}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{cat.slug}</td>
                  <td className="px-4 py-3 text-gray-500">
                    {cat.createdAt
                      ? new Date(cat.createdAt).toLocaleDateString()
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
